import { Hash, MessageCircle, MessagesSquare } from 'lucide-react';
import { Avatar } from '@/components/common/Avatar';
import type { Notification } from '@/types';
import { cn } from '@/lib/cn';
import { getChatNotificationContext } from '../utils/chatNotificationContext';

interface ChatNotificationContentProps {
  notification: Notification;
  timestamp?: string;
  compact?: boolean;
}

export function ChatNotificationContent({
  notification,
  timestamp,
  compact = false,
}: ChatNotificationContentProps): React.ReactElement | null {
  const context = getChatNotificationContext(notification);
  if (!context) return null;

  const SourceIcon =
    context.source === 'channel'
      ? Hash
      : context.source === 'group'
        ? MessagesSquare
        : MessageCircle;

  return (
    <span className="flex min-w-0 items-start gap-2.5">
      <Avatar
        name={context.senderName}
        src={context.senderAvatar}
        size={compact ? 'sm' : 'md'}
        className="mt-0.5"
      />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={cn(
              'truncate text-sm text-gray-800 dark:text-gray-200',
              !notification.isRead && 'font-semibold text-gray-950 dark:text-gray-100'
            )}
          >
            {context.senderName}
          </span>
          {context.mentioned && (
            <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
              Mentioned you
            </span>
          )}
        </span>
        <span className="mt-0.5 flex min-w-0 items-center gap-1 text-[11px] font-medium text-gray-500 dark:text-gray-400">
          <SourceIcon className="h-3 w-3 shrink-0" aria-hidden />
          <span className="truncate">{context.sourceLabel}</span>
        </span>
        {context.preview && (
          <span
            className={cn(
              'mt-1 block text-xs leading-5 text-gray-600 dark:text-gray-400',
              compact ? 'truncate' : 'line-clamp-2'
            )}
          >
            {context.preview}
          </span>
        )}
        {timestamp && (
          <span className="mt-1 block text-[11px] text-gray-400 dark:text-gray-500">
            {timestamp}
          </span>
        )}
      </span>
    </span>
  );
}

ChatNotificationContent.displayName = 'ChatNotificationContent';
